import React, { Children, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import useAccessControl from "./useAccessControl";
import { STORAGE_TOKEN } from "../constants";
import PageLoader from "../components/common/PageLoader";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SubscriptionProvider } from "../context/SubscriptionProvider";

interface ProtectedRouteProps {
  children: JSX.Element;
  requiredRoles: ("Accountant" | "Admin" | "Operator")[];
}

const queryClient = new QueryClient();

const ProtectedRoute = ({ children, requiredRoles }: ProtectedRouteProps) => {
  const { hasAccess } = useAccessControl();
  const location = useLocation();

  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchToken = async () => {
      const storedToken = await localStorage.getItem(STORAGE_TOKEN);
      setToken(storedToken);
      setLoading(false);
    };
    fetchToken();
  }, []);

  if (!token && !loading) {
    return <Navigate to={`/login?redirect=${location.pathname}`} replace />;
  }

  if (loading) {
    return (
      <div data-testid="loading-indicator">
        <PageLoader />
      </div>
    );
  }

  const rolesArray = Array.isArray(requiredRoles)
    ? requiredRoles
    : [requiredRoles];
  return hasAccess(rolesArray) ? (
    <QueryClientProvider client={queryClient}>
      <SubscriptionProvider>{children}</SubscriptionProvider>
    </QueryClientProvider>
  ) : (
    <Navigate to="/unauthorized" />
  );
};
export { ProtectedRoute };
